import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Phone, Mail, MapPin, Clock, Menu, X, ShoppingCart, 
  ChevronRight, Star, Trophy, GraduationCap, Sun, Wrench,
  Calendar, Users, Check, MessageCircle, ExternalLink,
  Facebook, Instagram, Download, Store, Truck
} from "lucide-react";
import AdminApp from "./AdminApp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ================== HEADER ==================
const Header = ({ activeSection, setShowCart, cartCount }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { id: "campamentos", label: "Campamentos" },
    { id: "instalaciones", label: "Instalaciones" },
    { id: "servicios", label: "Servicios" },
    { id: "tienda", label: "Tienda" },
    { id: "tarifas", label: "Tarifas" },
    { id: "contacto", label: "Contacto" },
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-black/5 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/au8d5gr7_don%20tello%20club%20de%20golf.jpg" 
              alt="Don Tello Club de Golf"
              className="h-14 w-auto"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                data-testid={`nav-${link.id}`}
                className={`text-sm font-medium transition-colors ${
                  activeSection === link.id
                    ? "text-green-600"
                    : "text-slate-600 hover:text-green-600"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Social icons - header */}
            <div className="hidden md:flex items-center gap-2">
              <a
                href="https://www.instagram.com/padeldontello/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-instagram"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-500 text-white hover:scale-110 transition-transform shadow-sm"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/alvaro.tarifaortiz"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-facebook"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 transition-transform shadow-sm"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
            
            <button 
              onClick={() => setShowCart(true)}
              data-testid="cart-button"
              className="relative p-2 text-slate-600 hover:text-green-600 transition-colors"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-600 text-white text-xs rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            
            <a
              href="https://playtomic.io/don-tello-golf/05d2dd18-4beb-43ac-bb99-aa64505d2abd"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="playtomic-booking-btn"
              className="hidden sm:flex items-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-green-700 transition-colors btn-glow"
            >
              <Calendar className="w-4 h-4" />
              Reservar Pista
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden glass border-t border-black/5" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left px-4 py-3 text-slate-600 hover:text-green-600 font-medium rounded-lg hover:bg-green-50 transition-colors"
              >
                {link.label}
              </button>
            ))}
            <a
              href="https://playtomic.io/don-tello-golf/05d2dd18-4beb-43ac-bb99-aa64505d2abd"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-green-600 text-white px-6 py-3 rounded-full font-semibold mt-4"
            >
              Reservar Pista
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

// ================== HERO ==================
const Hero = ({ onBuyBono }) => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-start overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/1kj7cc42_IMG_20180602_144303.jpg"
          alt="Pistas de Pádel Don Tello"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-2xl">
          <a 
            href="#arturo-coello"
            onClick={(e) => { e.preventDefault(); document.getElementById('sobre-nosotros')?.scrollIntoView({ behavior: 'smooth' }); }}
            data-testid="coello-badge"
            className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-md border border-yellow-400/40 rounded-full px-4 py-2 mb-6 animate-fade-in-up hover:bg-yellow-400/30 transition-colors cursor-pointer"
          >
            <Trophy className="w-4 h-4 text-yellow-300" />
            <span className="text-yellow-100 text-xs font-semibold tracking-wide">
              Club formador de <strong className="text-yellow-300">Arturo Coello</strong>, nº 1 del mundo
            </span>
          </a>

          {/* Shop delivery badge */}
          <a
            href="#tienda"
            onClick={(e) => { e.preventDefault(); document.getElementById('tienda')?.scrollIntoView({ behavior: 'smooth' }); }}
            data-testid="hero-shipping-badge"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-green-500/15 backdrop-blur-md border border-green-400/40 rounded-full px-4 py-2 mb-6 ml-0 sm:ml-2 animate-fade-in-up hover:bg-green-500/25 transition-colors cursor-pointer w-fit"
          >
            <span className="inline-flex items-center gap-1.5 text-green-100 text-xs font-semibold">
              <ShoppingCart className="w-3.5 h-3.5 text-green-300" />
              Visita la <strong className="text-green-300">TIENDA</strong>:
            </span>
            <span className="inline-flex items-center gap-1.5 text-green-100 text-xs font-semibold">
              <Store className="w-3.5 h-3.5 text-green-300" /> Recogida <strong className="text-green-300">gratis</strong> en el club.
            </span>
            <span className="inline-flex items-center gap-1.5 text-green-100 text-xs font-semibold">
              <Truck className="w-3.5 h-3.5 text-green-300" /> Envíos a la península <strong className="text-green-300">+7€</strong>
            </span>
          </a>
          <p className="text-green-400 font-bold text-sm tracking-[0.2em] uppercase mb-4 animate-fade-in-up delay-100">
            Club de Pádel y Tenis en Mérida
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight tracking-tighter mb-6 animate-fade-in-up delay-100">
            Tu club de
            <span className="text-green-400"> pádel</span> en el corazón de Extremadura
          </h1>
          <p className="text-lg text-white/80 mb-8 leading-relaxed animate-fade-in-up delay-200">
            5 pistas de pádel, pista de tenis de césped artificial, clases para todos los niveles y el mejor ambiente deportivo en el Club de Golf Don Tello.
          </p>
          
          <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
            <a
              href="https://playtomic.io/don-tello-golf/05d2dd18-4beb-43ac-bb99-aa64505d2abd"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-booking-btn"
              className="flex items-center gap-2 bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-green-700 transition-all btn-glow"
            >
              <Calendar className="w-5 h-5" />
              Reserva tu Pista
              <ChevronRight className="w-5 h-5" />
            </a>
            <button
              onClick={() => document.getElementById("instalaciones")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-explore-btn"
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all border border-white/20"
            >
              Explorar Club
            </button>
            <button
              onClick={onBuyBono}
              data-testid="hero-bono-btn"
              className="flex items-center gap-2 bg-amber-500 text-slate-900 px-8 py-4 rounded-full font-bold hover:bg-amber-400 transition-all shadow-lg"
            >
              🎾 Bono Monedero Playtomic
              <span className="text-xs bg-white/30 px-2 py-0.5 rounded-full">20€ → 22€</span>
            </button>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/20 animate-fade-in-up delay-400">
            <div>
              <p className="text-3xl font-black text-white">5</p>
              <p className="text-white/60 text-sm">Pistas de Pádel</p>
            </div>
            <div>
              <p className="text-3xl font-black text-white">1</p>
              <p className="text-white/60 text-sm">Pista de Tenis</p>
            </div>
            <div>
              <p className="text-3xl font-black text-white">25+</p>
              <p className="text-white/60 text-sm">Años de experiencia</p>
            </div>
            <div>
              <p className="text-3xl font-black text-white">500+</p>
              <p className="text-white/60 text-sm">Jugadores activos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/60 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

// ================== SUMMER CAMP (CAMPAMENTOS DE VERANO) ==================
const SummerCamp = ({ content }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!content || content.enabled === false) return null;

  const header = content.header || {};
  const actions = content.actions || {};
  const extras = content.extras || {};
  const courses = content.courses || {};
  const included = content.included || {};
  const prices = content.prices || {};
  const busStops = content.bus_stops || {};
  const recs = content.recommendations || {};
  const cta = content.cta || {};

  const courseColorMap = {
    green: { bg: "bg-green-100", title: "text-green-700", sub: "text-green-600" },
    amber: { bg: "bg-amber-100", title: "text-amber-700", sub: "text-amber-600" },
    blue:  { bg: "bg-blue-100",  title: "text-blue-700",  sub: "text-blue-600"  },
    purple:{ bg: "bg-purple-100",title: "text-purple-700",sub: "text-purple-600"},
    red:   { bg: "bg-red-100",   title: "text-red-700",   sub: "text-red-600"   },
  };

  return (
    <section id="campamentos" data-testid="summer-camp-section" className="py-20 bg-gradient-to-br from-yellow-400 via-orange-400 to-red-500 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full translate-x-1/2 translate-y-1/2"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        {header.visible !== false && (
          <div className="text-center mb-12">
            {header.badge && (
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold text-sm mb-4">
                {header.badge}
              </span>
            )}
            {header.title && (
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight">
                {header.title}
              </h2>
            )}
            {header.subtitle && (
              <p className="text-xl sm:text-2xl text-white/90 font-semibold">
                {header.subtitle}
              </p>
            )}
          </div>
        )}

        {/* INSCRIPCIONES + WhatsApp + Descargar cartel */}
        {actions.visible !== false && (
          <>
            {actions.inscription_url && (
              <div className="text-center mb-6">
                <a
                  href={actions.inscription_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="camp-inscription-btn"
                  className="inline-flex items-center gap-3 bg-green-600 text-white px-10 py-5 rounded-full font-black text-xl hover:bg-green-700 transition-all shadow-2xl hover:scale-105 animate-pulse-glow"
                >
                  <ExternalLink className="w-6 h-6" />
                  {actions.inscription_label || "INSCRIPCIONES ONLINE"}
                </a>
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {actions.whatsapp_phone && (
                <a
                  href={`https://wa.me/${actions.whatsapp_phone}?text=${encodeURIComponent(actions.whatsapp_text || "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="camp-whatsapp-btn"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full font-bold text-sm sm:text-base hover:bg-[#1ebe57] transition-all shadow-lg hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5" />
                  {actions.whatsapp_label || "Reservar por WhatsApp"}
                </a>
              )}
              {content.cartel1_url && (
                <button
                  onClick={async () => {
                    try {
                      const url = resolveAssetUrl(content.cartel1_url);
                      const res = await fetch(url);
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = blobUrl;
                      a.download = "Campamento-Don-Tello.png";
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(blobUrl);
                    } catch (e) {
                      window.open(resolveAssetUrl(content.cartel1_url), "_blank");
                    }
                  }}
                  data-testid="camp-download-flyer-btn"
                  className="inline-flex items-center gap-2 bg-white/95 text-orange-700 px-6 py-3 rounded-full font-bold text-sm sm:text-base hover:bg-white transition-all shadow-lg hover:scale-105 border-2 border-white"
                >
                  <Download className="w-5 h-5" />
                  {actions.flyer_label || "Descargar cartel"}
                </button>
              )}
            </div>
          </>
        )}

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Left - Cartel 1 + Opciones Extra */}
          <div className="flex flex-col gap-6">
            {content.cartel1_visible !== false && content.cartel1_url && (
              <img
                src={resolveAssetUrl(content.cartel1_url)}
                alt="Campamento de Verano Don Tello"
                className="w-full rounded-2xl shadow-2xl"
              />
            )}

            {extras.visible !== false && (extras.items || []).length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-purple-200">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Star className="w-6 h-6 text-purple-500" />
                  {extras.title || "Opciones Extras"}
                </h3>
                <table className="w-full text-sm">
                  <tbody>
                    {(extras.items || []).map((extra, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-3 text-slate-700 font-medium">{extra.name}</td>
                        <td className="py-3 text-right text-purple-600 font-bold whitespace-nowrap">{extra.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Right - Info cards */}
          <div className="flex flex-col gap-6">
            {courses.visible !== false && (courses.items || []).length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Star className="w-6 h-6 text-yellow-500" />
                  {courses.title || "Elige tu curso"}
                </h3>
                <div className={`grid gap-3 ${(courses.items || []).length > 2 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}>
                  {(courses.items || []).map((c, i) => {
                    const colors = courseColorMap[c.color] || courseColorMap.green;
                    return (
                      <div key={i} className={`${colors.bg} rounded-xl p-4 text-center`}>
                        <p className={`font-bold ${colors.title}`}>{c.name}</p>
                        <p className={`text-sm ${colors.sub}`}>{c.subtitle}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {included.visible !== false && (included.items || []).length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <h3 className="text-xl font-bold text-slate-900 mb-4">{included.title || "¿Qué incluye?"}</h3>
                <ul className="space-y-2">
                  {(included.items || []).map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {prices.visible !== false && (prices.rows || []).length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <h3 className="text-xl font-bold text-slate-900 mb-4">{prices.title || "Precios"}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2"></th>
                        <th className="text-center py-2 text-green-600 font-bold">{prices.col_member || "Abonados"}</th>
                        <th className="text-center py-2 text-slate-600 font-bold">{prices.col_non_member || "No abonados"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(prices.rows || []).map((row, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 font-medium">{row.weeks}</td>
                          <td className="text-center py-2 text-green-600 font-bold">{row.member}</td>
                          <td className="text-center py-2">{row.non_member}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {prices.note && (
                  <p className="mt-3 text-sm text-green-600 font-semibold">{prices.note}</p>
                )}
              </div>
            )}

            {/* Ver más info */}
            {(content.cartel2_visible !== false || busStops.visible !== false || recs.visible !== false) && (
              <button
                onClick={() => setShowDetails(!showDetails)}
                data-testid="camp-details-toggle"
                className="flex-1 bg-white text-orange-600 px-6 py-8 rounded-2xl font-bold hover:bg-orange-50 transition-colors shadow-xl border-2 border-orange-200 flex flex-col items-center justify-center gap-2 min-h-[100px]"
              >
                <span className="text-2xl text-center leading-tight">
                  {showDetails ? "Ocultar información" : "Ver más información"}
                </span>
                {!showDetails && (
                  <span className="text-base font-medium text-orange-500">
                    (paradas del bus y recomendaciones)
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Expandable details */}
        {showDetails && (
          <div className="space-y-6 mb-12 animate-fade-in-up">
            {content.cartel2_visible !== false && content.cartel2_url && (
              <img
                src={resolveAssetUrl(content.cartel2_url)}
                alt="Actividades Campamento Don Tello"
                className="w-full rounded-2xl shadow-2xl"
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {busStops.visible !== false && (busStops.items || []).length > 0 && (
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    {busStops.title || "Horarios del Autobús"}
                  </h3>
                  {busStops.subtitle && (
                    <p className="text-sm text-slate-600 mb-4">{busStops.subtitle}</p>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left py-2 px-2 font-bold text-blue-600">IDA</th>
                          <th className="text-left py-2 px-2 font-bold text-orange-600">VUELTA</th>
                          <th className="text-left py-2 px-2">Parada</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(busStops.items || []).map((stop, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2 px-2 font-bold text-blue-600">{stop.ida}</td>
                            <td className="py-2 px-2 font-bold text-orange-600">{stop.vuelta}</td>
                            <td className="py-2 px-2 text-slate-600 text-xs">{stop.place}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {recs.visible !== false && (
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-500" />
                    {recs.title || "Recomendaciones"}
                  </h3>
                  {recs.intro && (
                    <p className="text-slate-700 mb-4">{recs.intro}</p>
                  )}
                  <ul className="space-y-2">
                    {(recs.items || []).map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  {recs.footer_note && (
                    <p className="mt-5 pt-4 border-t text-slate-700 text-sm italic">{recs.footer_note}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CTA - Teléfonos */}
        {cta.visible !== false && (
          <div className="bg-white rounded-2xl p-6 shadow-2xl text-center max-w-3xl mx-auto">
            {cta.title && <h3 className="text-xl font-bold text-slate-900 mb-1">{cta.title}</h3>}
            {cta.subtitle && <p className="text-slate-600 text-sm mb-4">{cta.subtitle}</p>}
            {cta.phones_label && <p className="text-slate-700 font-semibold text-sm mb-3">{cta.phones_label}</p>}
            <div className="flex flex-wrap justify-center gap-2">
              {(cta.phones || []).map((phone, i) => (
                <a
                  key={i}
                  href={`tel:${phone.tel}`}
                  data-testid={`camp-phone-${i + 1}`}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full font-semibold text-sm hover:bg-green-700 transition-colors shadow"
                >
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">{phone.number}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// ================== FACILITY IMAGE ROTATOR ==================
const FacilityCard = ({ facility, isLarge }) => {
  const images = facility.images && facility.images.length > 0 ? facility.images : [facility.image];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (images.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length, isPaused]);

  const goNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goPrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div
      data-testid={`facility-card-${facility.id}`}
      className={`group relative overflow-hidden rounded-2xl card-hover ${
        isLarge ? "md:col-span-2 md:row-span-2" : ""
      }`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className={`relative ${isLarge ? "h-[500px]" : "h-[238px]"}`}>
        {images.map((img, i) => (
          <img
            key={i}
            src={img}
            alt={facility.title}
            className={`absolute inset-0 w-full h-full object-cover ${
              i === currentIndex ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
            style={{ transition: "opacity 0.8s ease-in-out" }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none z-20"></div>
        
        {/* Navigation arrows for multi-image */}
        {images.length > 1 && (
          <>
            <button
              onClick={goPrev}
              data-testid={`facility-prev-${facility.id}`}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/80 hover:bg-white backdrop-blur-sm rounded-full flex items-center justify-center text-slate-900 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Foto anterior"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <button
              onClick={goNext}
              data-testid={`facility-next-${facility.id}`}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/80 hover:bg-white backdrop-blur-sm rounded-full flex items-center justify-center text-slate-900 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Siguiente foto"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        
        <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none z-20">
          <h3 className="text-xl font-bold text-white mb-2">{facility.title}</h3>
          <p className="text-white/80 text-sm">{facility.description}</p>
        </div>
        
        {/* Indicator dots for multi-image */}
        {images.length > 1 && (
          <div className="absolute top-4 right-4 z-30 flex gap-1.5 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(i);
                }}
                className={`block h-2 rounded-full transition-all ${
                  i === currentIndex ? "bg-white w-6" : "bg-white/50 w-2 hover:bg-white/70"
                }`}
                aria-label={`Ir a foto ${i + 1}`}
              />
            ))}
          </div>
        )}
        
        {/* Image counter badge */}
        {images.length > 1 && (
          <div className="absolute top-4 left-4 z-30 bg-black/50 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
};

// ================== PROMO BANNER (editable) ==================
const resolveAssetUrl = (url) => {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
};

const PromoBanner = ({ banner }) => {
  // Filter slides by schedule (starts_at / ends_at)
  const now = new Date();
  const allSlides = (banner && banner.slides) || [];
  const slides = allSlides.filter((s) => {
    const startOk = !s.starts_at || new Date(s.starts_at) <= now;
    const endOk = !s.ends_at || new Date(s.ends_at) >= now;
    return startOk && endOk;
  });
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!banner?.enabled || slides.length <= 1 || paused) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(t);
  }, [banner?.enabled, slides.length, paused]);

  // Reset index when slides change
  useEffect(() => {
    if (idx >= slides.length) setIdx(0);
  }, [slides.length, idx]);

  if (!banner || !banner.enabled || slides.length === 0) return null;

  const bgMap = {
    green: "from-green-600 to-emerald-500",
    amber: "from-amber-500 to-orange-500",
    dark: "from-slate-900 to-slate-700",
    red: "from-red-600 to-rose-500",
  };

  const ytEmbed = (url) => {
    const m = (url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : url;
  };

  const current = slides[Math.min(idx, slides.length - 1)] || {};
  const bg = bgMap[current.background || "green"] || bgMap.green;
  const isYoutube = current.media_type === "youtube" && current.media_url;
  const isVideo = current.media_type === "video" && current.media_url;
  const isImage = (!current.media_type || current.media_type === "image") && current.media_url;
  const src = resolveAssetUrl(current.media_url);
  const hasMedia = !!current.media_url;
  const multi = slides.length > 1;

  const go = (n) => setIdx(((n % slides.length) + slides.length) % slides.length);

  return (
    <section
      id="promo-banner"
      data-testid="promo-banner"
      className={`relative bg-gradient-to-r ${bg} py-10 sm:py-14 transition-colors duration-700`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center animate-fade-in-up">
          {hasMedia && (
            <div className="flex items-center justify-center">
              {isYoutube && (
                <div className="w-full rounded-2xl overflow-hidden shadow-2xl aspect-video bg-black">
                  <iframe
                    className="w-full h-full"
                    src={ytEmbed(current.media_url)}
                    title="Promo"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />
                </div>
              )}
              {isVideo && (
                <video src={src} controls autoPlay muted loop playsInline
                  className="max-h-[450px] w-auto max-w-full rounded-2xl shadow-2xl bg-black" />
              )}
              {isImage && (
                <img src={src} alt={current.title || "Promo"}
                  className="max-h-[450px] w-auto max-w-full rounded-2xl shadow-2xl bg-white/10" />
              )}
            </div>
          )}
          <div className={`text-white ${!hasMedia ? "md:col-span-2 text-center" : ""}`}>
            {current.title && <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-3">{current.title}</h2>}
            {current.subtitle && <p className="text-white/90 text-base sm:text-lg leading-relaxed whitespace-pre-line">{current.subtitle}</p>}
            {current.cta_label && current.cta_url && (
              <a
                href={current.cta_url}
                target={current.cta_url.startsWith("http") ? "_blank" : "_self"}
                rel="noopener noreferrer"
                data-testid="promo-cta"
                className="inline-flex items-center gap-2 mt-5 bg-white text-slate-900 px-7 py-3 rounded-full font-bold hover:bg-slate-100 transition-colors shadow-lg"
              >
                {current.cta_label}
              </a>
            )}
          </div>
        </div>

        {/* Carousel controls */}
        {multi && (
          <>
            <button
              onClick={() => go(idx - 1)}
              aria-label="Anterior"
              data-testid="promo-prev"
              className="absolute left-1 sm:left-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors z-10"
            >
              <ChevronRight className="w-6 h-6 rotate-180" />
            </button>
            <button
              onClick={() => go(idx + 1)}
              aria-label="Siguiente"
              data-testid="promo-next"
              className="absolute right-1 sm:right-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors z-10"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="flex justify-center gap-2 mt-6">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  aria-label={`Ir a slide ${i + 1}`}
                  data-testid={`promo-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all ${i === idx ? "bg-white w-8" : "bg-white/50 hover:bg-white/70 w-2.5"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// ================== FACILITIES ==================
const Facilities = ({ facilities }) => {
  return (
    <section id="instalaciones" data-testid="facilities-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm sm:text-base tracking-[0.2em] uppercase font-bold text-green-600 mb-3">
            Nuestras Instalaciones
          </p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
            Todo lo que necesitas para disfrutar
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Ubicados dentro del Club de Golf Don Tello, ofrecemos instalaciones de primer nivel para la práctica del pádel y tenis.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {facilities.map((facility, index) => (
            <FacilityCard key={facility.id} facility={facility} isLarge={index === 0} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ================== SERVICES ==================
const Services = ({ services }) => {
  const iconMap = {
    GraduationCap: GraduationCap,
    Trophy: Trophy,
    Sun: Sun,
    Wrench: Wrench,
  };
  const [modalService, setModalService] = useState(null);

  return (
    <>
    <section id="servicios" data-testid="services-section" className="py-24 bg-[#F4F7F4] bg-pattern">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm sm:text-base tracking-[0.2em] uppercase font-bold text-green-600 mb-3">
            Servicios
          </p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
            Más que un club de pádel
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Ofrecemos una experiencia completa con clases, torneos, equipos de competición y servicios especializados.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || Star;
            const hasImage = !!service.image;
            const hasUrl = !!service.url;
            
            const cardInner = (
              <>
                {hasImage ? (
                  <div className="relative w-24 h-24 mb-6 rounded-xl overflow-hidden border-2 border-green-100 shadow-md">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="w-7 h-7 text-green-600" />
                  </div>
                )}
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {service.title}
                  {hasUrl ? (
                    <span className="block text-xs text-orange-600 font-normal mt-1 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" />
                      Click para ir a Wallapop
                    </span>
                  ) : hasImage && (
                    <span className="block text-xs text-green-600 font-normal mt-1">Click para ver en grande</span>
                  )}
                </h3>
                <p className="text-slate-600 text-sm mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-green-600" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </>
            );
            
            if (hasUrl) {
              return (
                <a
                  key={service.id}
                  href={service.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-card-${service.id}`}
                  className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 card-hover cursor-pointer block"
                >
                  {cardInner}
                </a>
              );
            }
            
            return (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                onClick={() => hasImage && setModalService(service)}
                className={`bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 card-hover ${hasImage ? "cursor-pointer" : ""}`}
              >
                {cardInner}
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Service image modal */}
    {modalService && (
      <div 
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={() => setModalService(null)}
        data-testid="service-modal"
      >
        <button 
          onClick={() => setModalService(null)}
          className="absolute top-6 right-6 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="max-w-4xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
          <img 
            src={modalService.image}
            alt={modalService.title}
            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          />
          <div className="mt-4 bg-white rounded-xl px-6 py-3 text-center">
            <h4 className="font-bold text-slate-900">{modalService.title}</h4>
            <p className="text-slate-600 text-sm mt-1">{modalService.description}</p>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

// ================== PRODUCT OPTIONS MODAL ==================
const ProductOptionsModal = ({ product, onClose, onConfirm }) => {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  useEffect(() => {
    if (product?.options) {
      const initial = {};
      product.options.forEach(opt => { initial[opt.name] = opt.values[0]; });
      setSelectedOptions(initial);
    } else {
      setSelectedOptions({});
    }
    if (product?.variants && product.variants.length > 0) {
      setSelectedVariant(product.variants[0]);
    } else {
      setSelectedVariant(null);
    }
  }, [product]);

  if (!product) return null;

  const hasOptions = product.options && product.options.length > 0;
  const hasVariants = product.variants && product.variants.length > 0;
  const finalPrice = selectedVariant ? selectedVariant.price : product.price;
  const isReady = (!hasOptions || Object.keys(selectedOptions).length === product.options.length) &&
                  (!hasVariants || selectedVariant);

  const handleConfirm = (deliveryMethod) => {
    onConfirm({
      ...product,
      selectedOptions,
      selectedVariant,
      price: finalPrice,
      name: selectedVariant ? `${product.name} - ${selectedVariant.name}` : product.name
    }, deliveryMethod);
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="product-options-modal"
    >
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <img 
            src={resolveAssetUrl(product.image)} 
            alt={product.name}
            className="w-full h-64 object-contain bg-slate-50 p-4"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center text-slate-600 shadow-md hover:bg-slate-100"
            data-testid="close-options-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <h3 className="text-2xl font-bold text-slate-900 mb-2">{product.name}</h3>
          <p className="text-slate-600 text-sm mb-6">{product.description}</p>

          {/* Variants (e.g., Bolas with different prices) */}
          {hasVariants && (
            <div className="mb-6">
              <h4 className="font-bold text-slate-900 mb-3">Elige tu marca</h4>
              <div className="grid grid-cols-1 gap-2">
                {product.variants.map((variant) => (
                  <button
                    key={variant.name}
                    onClick={() => setSelectedVariant(variant)}
                    data-testid={`variant-${variant.name}`}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all ${
                      selectedVariant?.name === variant.name
                        ? "border-green-600 bg-green-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <span className="font-medium text-slate-800">{variant.name}</span>
                    <span className={`font-bold ${selectedVariant?.name === variant.name ? "text-green-600" : "text-slate-600"}`}>
                      {variant.price}€
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Options (e.g., Equipación) */}
          {hasOptions && product.options.map((opt) => (
            <div key={opt.name} className="mb-5">
              <h4 className="font-bold text-slate-900 mb-3">{opt.name}</h4>
              <div className="flex flex-wrap gap-2">
                {opt.values.map((value) => (
                  <button
                    key={value}
                    onClick={() => setSelectedOptions({...selectedOptions, [opt.name]: value})}
                    data-testid={`option-${opt.name}-${value}`}
                    className={`px-5 py-2 rounded-full border-2 font-semibold text-sm transition-all ${
                      selectedOptions[opt.name] === value
                        ? "border-green-600 bg-green-600 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Footer with price + delivery method choice */}
          <div className="border-t pt-4 mt-6">
            {(() => {
              const pickupOnly = product.category === "pelotas" || product.category === "overgrips";
              if (pickupOnly) {
                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs text-slate-500">Precio</p>
                        <p className="text-3xl font-black text-green-600">{finalPrice}€</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleConfirm("pickup")}
                      disabled={!isReady}
                      data-testid="confirm-add-to-cart-pickup"
                      className="w-full inline-flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-full font-bold text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Store className="w-4 h-4" /> Añadir · Solo en tienda
                    </button>
                    <p className="text-[11px] text-slate-500 mt-2 text-center">
                      Disponibles en la caseta del club. Sin envío disponible para este producto.
                    </p>
                  </>
                );
              }
              return (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs text-slate-500">Precio</p>
                      <p className="text-3xl font-black text-green-600">{finalPrice}€</p>
                    </div>
                    <p className="text-xs text-slate-500 text-right max-w-[180px]">
                      ¿Cómo lo quieres?
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleConfirm("pickup")}
                      disabled={!isReady}
                      data-testid="confirm-add-to-cart-pickup"
                      className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-full font-bold text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Store className="w-4 h-4" /> En tienda
                    </button>
                    <button
                      onClick={() => handleConfirm("shipping")}
                      disabled={!isReady}
                      data-testid="confirm-add-to-cart-shipping"
                      className="inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-900 border-2 border-slate-300 px-4 py-3 rounded-full font-bold text-sm hover:bg-green-600 hover:text-white hover:border-green-600 transition-colors disabled:opacity-50"
                    >
                      <Truck className="w-4 h-4" /> Envío +7€
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2 text-center">En tienda: recoge gratis en el Club Don Tello.</p>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

// ================== SHOP ==================
const Shop = ({ products, addToCart }) => {
  const categories = [
    { id: "all", label: "TODO" },
    { id: "equipacion", label: "EQUIPACION DEL CLUB" },
    { id: "palas", label: "PALAS" },
    { id: "pelotas", label: "PELOTAS" },
    { id: "overgrips", label: "OVERGRIPS" },
    { id: "reparacion", label: "REPARACION DE PALA" },
  ];
  const [activeCategory, setActiveCategory] = useState("all");
  const [zoomImage, setZoomImage] = useState(null);
  const [optionsProduct, setOptionsProduct] = useState(null);
  
  // Order products: equipacion, pelotas, overgrips, reparacion, palas (1), ver todas
  const categoryOrder = { equipacion: 1, pelotas: 2, overgrips: 3, reparacion: 4, palas: 5 };
  const sortedProducts = [...products].sort((a, b) => 
    (categoryOrder[a.category] || 99) - (categoryOrder[b.category] || 99)
  );
  
  const filteredProducts = activeCategory === "all" 
    ? sortedProducts 
    : sortedProducts.filter(p => p.category === activeCategory);

  const showViewAllPalas = activeCategory === "all" || activeCategory === "palas";

  return (
    <>
    <section id="tienda" data-testid="shop-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm sm:text-base tracking-[0.2em] uppercase font-bold text-green-600 mb-3">
            Tienda
          </p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
            Equipamiento y accesorios
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Palas, equipación y accesorios a la venta. Compra online con envío o recógelos en el club.
          </p>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`shop-cat-${cat.id}`}
              className={`px-5 py-2 rounded-full font-semibold text-sm transition-all ${
                activeCategory === cat.id
                  ? "bg-green-600 text-white shadow-lg"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              data-testid={`product-card-${product.id}`}
              className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden card-hover flex flex-col"
            >
              <div 
                className={`relative h-56 bg-slate-100 ${product.category === "equipacion" ? "cursor-zoom-in" : ""}`}
                onClick={() => product.category === "equipacion" && setZoomImage(product)}
              >
                <img
                  src={resolveAssetUrl(product.image)}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
                {product.url && product.category !== "palas" && (
                  <span className="absolute top-3 left-3 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full shadow-lg">
                    WALLAPOP
                  </span>
                )}
                {product.in_stock === false && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="px-4 py-2 bg-red-600 text-white text-sm font-black rounded-full shadow-lg">VENDIDA</span>
                  </div>
                )}
                {product.category === "equipacion" && (
                  <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                    Click para ampliar
                  </span>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-base font-bold text-slate-900 mb-2 line-clamp-2">{product.name}</h3>
                <p className="text-slate-500 text-xs mb-4 line-clamp-3 flex-1">{product.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-black text-green-600">
                    {product.category === "pelotas" ? `desde ${product.price}€` : 
                     product.category === "reparacion" ? `desde ${product.price}€` : 
                     product.category === "overgrips" ? `${product.price}€/ud` :
                     `${product.price}€`}
                  </span>
                  {product.in_stock === false ? (
                    <span className="text-sm text-slate-400 font-semibold">Agotada</span>
                  ) : product.category === "clases" ? (
                    <a
                      href="tel:+34639713537"
                      data-testid={`call-${product.id}`}
                      className="inline-flex items-center gap-1 bg-green-600 text-white px-4 py-2 rounded-full font-semibold text-sm hover:bg-green-700 transition-colors"
                    >
                      <Phone className="w-3 h-3" />
                      Llamar
                    </a>
                  ) : product.category === "palas" || product.category === "bonos" ? (
                    product.category === "bonos" ? (
                      <button
                        onClick={() => addToCart(product, "pickup")}
                        data-testid={`add-to-cart-${product.id}`}
                        className="bg-green-600 text-white px-4 py-2 rounded-full font-semibold text-sm hover:bg-green-700 transition-colors"
                      >
                        Comprar
                      </button>
                    ) : (
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => addToCart(product, "pickup")}
                          data-testid={`buy-pickup-${product.id}`}
                          title="Recoger en el club · Sin gastos de envío"
                          className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-2 rounded-full font-semibold text-xs hover:bg-green-700 transition-colors"
                        >
                          <Store className="w-3.5 h-3.5" /> Tienda
                        </button>
                        <button
                          onClick={() => addToCart(product, "shipping")}
                          data-testid={`buy-shipping-${product.id}`}
                          title="Envío a domicilio · +7€"
                          className="inline-flex items-center gap-1 bg-slate-100 text-slate-900 px-3 py-2 rounded-full font-semibold text-xs hover:bg-green-600 hover:text-white transition-colors border border-slate-300"
                        >
                          <Truck className="w-3.5 h-3.5" /> Envío +7€
                        </button>
                      </div>
                    )
                  ) : product.url ? (
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`view-product-${product.id}`}
                      className="flex items-center gap-1 bg-orange-500 text-white px-4 py-2 rounded-full font-semibold text-sm hover:bg-orange-600 transition-colors"
                    >
                      Ver
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (product.options || product.variants) ? (
                    <button
                      onClick={() => setOptionsProduct(product)}
                      data-testid={`select-options-${product.id}`}
                      className="bg-green-600 text-white px-4 py-2 rounded-full font-semibold text-sm hover:bg-green-700 transition-colors"
                    >
                      Elegir
                    </button>
                  ) : product.category === "equipacion" ? (
                    <button
                      onClick={() => setZoomImage(product)}
                      data-testid={`view-equipacion`}
                      className="bg-slate-100 text-slate-900 px-4 py-2 rounded-full font-semibold text-sm hover:bg-green-600 hover:text-white transition-colors"
                    >
                      Ver detalles
                    </button>
                  ) : (
                    product.category === "overgrips" ? (
                      <button
                        onClick={() => addToCart(product, "pickup")}
                        data-testid={`add-to-cart-${product.id}`}
                        title="Disponibles en la caseta del club"
                        className="inline-flex items-center gap-1 bg-green-600 text-white px-4 py-2 rounded-full font-semibold text-xs hover:bg-green-700 transition-colors"
                      >
                        <Store className="w-3.5 h-3.5" /> Solo en tienda
                      </button>
                    ) : (
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => addToCart(product, "pickup")}
                          data-testid={`buy-pickup-${product.id}`}
                          title="Recoger en el club · Sin gastos de envío"
                          className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-2 rounded-full font-semibold text-xs hover:bg-green-700 transition-colors"
                        >
                          <Store className="w-3.5 h-3.5" /> Tienda
                        </button>
                        <button
                          onClick={() => addToCart(product, "shipping")}
                          data-testid={`buy-shipping-${product.id}`}
                          title="Envío a domicilio · +7€"
                          className="inline-flex items-center gap-1 bg-slate-100 text-slate-900 px-3 py-2 rounded-full font-semibold text-xs hover:bg-green-600 hover:text-white transition-colors border border-slate-300"
                        >
                          <Truck className="w-3.5 h-3.5" /> Envío +7€
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* View all palas card - FEATURED pala as hook */}
          {showViewAllPalas && (
            <a
              href="https://es.wallapop.com/user/dontellopadel-98391301"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-all-palas"
              className="relative rounded-2xl overflow-hidden shadow-xl card-hover group sm:col-span-2 lg:col-span-2"
            >
              {/* Background image - most expensive pala */}
              <div className="relative h-full min-h-[400px]">
                <img
                  src="https://cdn.wallapop.com/images/10420/kp/k4/__/c10420p1252253347/i6433965319.jpg?pictureSize=W640"
                  alt="Pala Nox AT10 Genius 12K Alum Xtreme Tapia"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Dark gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                
                {/* Price badge - top right */}
                <div className="absolute top-4 right-4 bg-orange-500 text-white font-black text-2xl px-5 py-2 rounded-full shadow-2xl border-2 border-white">
                  235€
                </div>
                
                {/* Wallapop badge - top left */}
                <div className="absolute top-4 left-4 bg-white text-orange-600 font-black text-xs px-3 py-1.5 rounded-full shadow-lg">
                  WALLAPOP
                </div>

                {/* Content - bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <p className="text-sm font-semibold text-orange-300 mb-1 uppercase tracking-wide">Catálogo completo</p>
                  <h3 className="text-3xl font-black mb-3 leading-tight">
                    ¿No encuentras tu pala? Mira todo nuestro stock en Wallapop
                  </h3>
                  <div className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-full font-bold text-base shadow-xl group-hover:bg-orange-600 transition-colors">
                    VER MÁS EN WALLAPOP
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </a>
          )}
        </div>
      </div>
    </section>

    {/* Product Options Modal */}
    <ProductOptionsModal
      product={optionsProduct}
      onClose={() => setOptionsProduct(null)}
      onConfirm={(productWithOpts, deliveryMethod) => addToCart(productWithOpts, deliveryMethod)}
    />

    {/* Zoom Image Modal */}
    {zoomImage && (
      <div 
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={() => setZoomImage(null)}
        data-testid="zoom-modal"
      >
        <button 
          onClick={() => setZoomImage(null)}
          className="absolute top-6 right-6 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
          data-testid="close-zoom"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
          <img 
            src={zoomImage.image}
            alt={zoomImage.name}
            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          />
          <div className="mt-4 bg-white rounded-xl px-6 py-3 text-center max-w-2xl">
            <h4 className="font-bold text-slate-900">{zoomImage.name}</h4>
            <p className="text-green-600 font-black text-2xl mt-1">{zoomImage.price}€</p>
          </div>
        </div>
      </div>
    )}
    </>
  );
};


// ================== PRICING ==================
const Pricing = ({ pricing, pricingPdfUrl }) => {
  return (
    <section id="tarifas" data-testid="pricing-section" className="py-24 bg-[#064E3B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm sm:text-base tracking-[0.2em] uppercase font-bold text-green-400 mb-3">
            Tarifas
          </p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white mb-4">
            Precios accesibles para todos
          </h2>
          <p className="text-green-200 max-w-2xl mx-auto">
            Horario: Lunes a Domingo de 8:00h a 24:00h
          </p>
        </div>

        {/* Featured Offer Banner */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-6 mb-6 shadow-2xl">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
            <div className="flex items-center gap-2">
              <Star className="w-8 h-8 text-white" />
              <span className="text-white font-black text-xl uppercase tracking-wide">Oferta destacada</span>
            </div>
            <div className="text-white">
              <p className="text-lg md:text-xl font-bold">
                Reservas de Pádel: <span className="bg-white text-orange-600 px-3 py-1 rounded-full">14€ tardes</span> · <span className="bg-white text-orange-600 px-3 py-1 rounded-full">12€ mañanas y fines de semana</span>
              </p>
            </div>
          </div>
        </div>

        {/* Discount Banner for Members & Students */}
        <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl p-5 mb-10 shadow-2xl border-2 border-white/30">
          <div className="flex items-center justify-center gap-3 text-center">
            <Trophy className="w-8 h-8 text-white flex-shrink-0" />
            <p className="text-white font-black text-lg md:text-xl uppercase tracking-wide">
              Grandes descuentos a socios y alumnos de Don Tello Golf
            </p>
            <Trophy className="w-8 h-8 text-white flex-shrink-0" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {pricing.map((plan) => (
            <div
              key={plan.id}
              data-testid={`pricing-card-${plan.id}`}
              className={`rounded-2xl p-8 ${
                plan.highlighted
                  ? "bg-white shadow-2xl scale-105"
                  : "bg-white/10 backdrop-blur-sm border border-white/10"
              }`}
            >
              {plan.highlighted && (
                <span className="inline-block px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full mb-4">
                  Más popular
                </span>
              )}
              <h3 className={`text-xl font-bold mb-2 ${plan.highlighted ? "text-slate-900" : "text-white"}`}>
                {plan.name}
              </h3>
              <div className="mb-6">
                <span className={`text-sm font-semibold mr-1 ${plan.highlighted ? "text-slate-500" : "text-green-300"}`}>DESDE</span>
                <span className={`text-4xl font-black ${plan.highlighted ? "text-green-600" : "text-white"}`}>
                  {plan.price}
                </span>
                <span className={plan.highlighted ? "text-slate-600" : "text-green-200"}>
                  {plan.period}
                </span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li 
                    key={i} 
                    className={`flex items-center gap-2 text-sm ${
                      plan.highlighted ? "text-slate-600" : "text-green-100"
                    }`}
                  >
                    <Check className={`w-4 h-4 ${plan.highlighted ? "text-green-600" : "text-green-400"}`} />
                    {feature}
                  </li>
                ))}
              </ul>
              <a
                href="https://playtomic.io/don-tello-golf/05d2dd18-4beb-43ac-bb99-aa64505d2abd"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`pricing-cta-${plan.id}`}
                className={`block w-full text-center py-3 rounded-full font-semibold transition-colors ${
                  plan.highlighted
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-white text-green-900 hover:bg-green-50"
                }`}
              >
                Reservar
              </a>
            </div>
          ))}
        </div>

        {/* Listado de precios button */}
        <div className="text-center">
          <a
            href={pricingPdfUrl || "https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/nnc8scfx_tarifas.pdf"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="full-pricing-btn"
            className="inline-flex items-center gap-3 bg-white text-green-900 px-10 py-4 rounded-full font-bold text-lg hover:bg-green-50 transition-colors shadow-2xl"
          >
            <ExternalLink className="w-5 h-5" />
            LISTADO COMPLETO DE PRECIOS (PDF)
          </a>
        </div>
      </div>
    </section>
  );
};

// ================== CONTACT ==================
const Contact = ({ clubInfo }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm sm:text-base tracking-[0.2em] uppercase font-bold text-green-600 mb-3">
            Contacto
          </p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
            ¿Tienes alguna pregunta?
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Estamos aquí para ayudarte. Contáctanos y te responderemos lo antes posible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-[#F4F7F4] rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">¡Mensaje enviado!</h3>
                <p className="text-slate-600">Te responderemos lo antes posible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Nombre
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="contact-name-input"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-green-600 focus:ring-2 focus:ring-green-600/20 outline-none transition-all"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="contact-email-input"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-green-600 focus:ring-2 focus:ring-green-600/20 outline-none transition-all"
                        placeholder="tu@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        data-testid="contact-phone-input"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-green-600 focus:ring-2 focus:ring-green-600/20 outline-none transition-all"
                        placeholder="600 000 000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Mensaje
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      data-testid="contact-message-input"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-green-600 focus:ring-2 focus:ring-green-600/20 outline-none transition-all resize-none"
                      placeholder="¿En qué podemos ayudarte?"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-green-600 text-white py-4 rounded-full font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? "Enviando..." : "Enviar mensaje"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Map & Info */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-[#F4F7F4] rounded-2xl p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{clubInfo.address}</p>
                    <p className="text-slate-600 text-sm">{clubInfo.city}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <a href={`tel:${clubInfo.phone}`} className="font-semibold text-slate-900 hover:text-green-600">
                      {clubInfo.phone}
                    </a>
                    <p className="text-slate-600 text-sm">{clubInfo.contact_person}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Horario</p>
                    <p className="text-slate-600 text-sm">L-V: {clubInfo.hours?.weekdays} | S-D: {clubInfo.hours?.weekends}</p>
                  </div>
                </div>
                
                {/* Redes sociales */}
                <div className="pt-4 border-t">
                  <p className="font-semibold text-slate-900 mb-3">Síguenos en redes</p>
                  <div className="flex gap-3">
                    <a
                      href="https://www.instagram.com/padeldontello/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-instagram"
                      className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-500 text-white font-semibold hover:opacity-90 transition-opacity shadow-md"
                    >
                      <Instagram className="w-5 h-5" />
                      @padeldontello
                    </a>
                    <a
                      href="https://www.facebook.com/alvaro.tarifaortiz"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-facebook"
                      className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1877F2] text-white font-semibold hover:bg-[#166fe5] transition-colors shadow-md"
                    >
                      <Facebook className="w-5 h-5" />
                      Facebook
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-2xl overflow-hidden h-[300px]" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3121.9!2d-6.3438!3d38.8735!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDUyJzI0LjYiTiA2wrAyMCczNy43Ilc!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación Don Tello Padel"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ================== ABOUT US ==================
const AboutUs = () => {
  const [activeTab, setActiveTab] = useState("cv");
  const [lightboxImage, setLightboxImage] = useState(null);
  
  const coelloPhotos = [
    "https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/8ldl3su0_11119426_729799623813561_8455345314602831355_o.jpg",
    "https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/qlepabli_20160312_181501.jpg",
    "https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/mkih8zb9_20180117_210120.jpg",
    "https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/1ukzmtgo_verano2017-.jpg",
  ];
  
  return (
    <>
    <section id="sobre-nosotros" data-testid="about-us-section" className="py-20 bg-gradient-to-br from-slate-50 to-green-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          <button
            onClick={() => setActiveTab("cv")}
            data-testid="tab-cv"
            className={`px-6 py-3 rounded-full font-bold text-sm transition-all ${
              activeTab === "cv" 
                ? "bg-green-600 text-white shadow-lg" 
                : "bg-white text-slate-700 hover:bg-slate-100"
            }`}
          >
            Sobre Nosotros
          </button>
          <button
            onClick={() => setActiveTab("coello")}
            data-testid="tab-coello"
            className={`px-6 py-3 rounded-full font-bold text-sm transition-all ${
              activeTab === "coello" 
                ? "bg-yellow-400 text-yellow-900 shadow-lg" 
                : "bg-white text-slate-700 hover:bg-slate-100"
            }`}
          >
            🏆 Arturo Coello en Don Tello
          </button>
        </div>
        
        {/* CV Tab */}
        {activeTab === "cv" && (
          <div className="bg-white rounded-2xl p-8 shadow-xl animate-fade-in-up">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900">Álvaro Tarifa Ortiz</h3>
              <p className="text-green-600 font-semibold">Director Deportivo del Club</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
                <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                  <Trophy className="w-5 h-5" />
                  Fed. Española de Pádel
                </h4>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Monitor nacional</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Árbitro nacional</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Profesor cursos Juez Árbitro FEP</li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <Trophy className="w-5 h-5" />
                  Fed. Extremeña de Pádel
                </h4>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />Presidente Comité Árbitros</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />Vocal Junta Directiva</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />Profesor cursos Juez Árbitro Autonómico</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />Campeón Extremadura Vet. +40</li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                <h4 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
                  <Trophy className="w-5 h-5" />
                  Fed. Española de Tenis
                </h4>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />Monitor nacional</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl p-5 text-center shadow-lg">
              <p className="text-white font-bold text-lg">
                🏆 Entrenador de Arturo Coello y Rodrigo Coello (2014-2018)
              </p>
            </div>

            {/* Foto icónica Mundial 2017 */}
            <div className="mt-6 rounded-xl overflow-hidden shadow-xl group cursor-pointer relative" onClick={() => setLightboxImage("https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/d5mc9pa9_mundial%202017.jpg")} data-testid="mundial-2017-photo">
              <img
                src="https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/d5mc9pa9_mundial%202017.jpg"
                alt="Álvaro Tarifa coach de Arturo Coello en el Mundial de Menores 2017"
                className="w-full max-h-[520px] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-5">
                <p className="text-white font-bold text-base sm:text-lg">
                  Álvaro Tarifa como coach de Arturo Coello
                </p>
                <p className="text-white/85 text-sm">
                  Mundial de Menores · 2017
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Coello Tab */}
        {activeTab === "coello" && (
          <div className="bg-white rounded-2xl p-8 shadow-xl animate-fade-in-up">
            <div className="flex flex-col sm:flex-row items-center gap-5 mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/vunba2fa_000foto%20wpt%20arturo%20coello%20manso.jpg"
                alt="Arturo Coello con la camiseta del Club Don Tello"
                data-testid="coello-thumbnail"
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-yellow-400 shadow-lg flex-shrink-0"
              />
              <div className="text-center sm:text-left flex-1">
                <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-900 px-4 py-2 rounded-full mb-2">
                  <Trophy className="w-5 h-5" />
                  <span className="font-bold text-sm">Nº 1 DEL MUNDO</span>
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Arturo Coello en Don Tello</h3>
                <p className="text-slate-600 mt-2 max-w-2xl">
                  Entre 2014 y 2018, el actual número 1 del mundo Arturo Coello entrenó junto a su hermano Rodrigo en las pistas de nuestro club. Un orgullo haber formado parte de su camino.
                </p>
              </div>
            </div>

            {/* Embedded featured video */}
            <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-xl mb-6 bg-black" data-testid="coello-video-embed">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/Xx-SvsyXqzw"
                title="Arturo Coello entrenando en Don Tello"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            {/* Photos grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {coelloPhotos.map((photo, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxImage(photo)}
                  data-testid={`coello-photo-${i}`}
                  className="relative aspect-square overflow-hidden rounded-xl group cursor-pointer"
                >
                  <img 
                    src={photo}
                    alt={`Arturo Coello ${i + 1}`}
                    className="w-full h-full object-cover transition-transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                </button>
              ))}
            </div>
            
            {/* YouTube video links grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { url: "https://youtu.be/f_4BMeM3WFA", label: "Vídeo · Coello en Don Tello (1)" },
                { url: "https://youtu.be/r-kchF8YGDs", label: "Vídeo · Coello en Don Tello (2)" },
                { url: "https://youtu.be/abI_QUdsF9w", label: "Vídeo · Coello en Don Tello (3)" },
              ].map((v, i) => (
                <a
                  key={i}
                  href={v.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`coello-video-${i}`}
                  className="flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-3 rounded-full font-bold text-sm hover:bg-red-700 transition-colors shadow-lg"
                >
                  <ExternalLink className="w-4 h-4" />
                  {v.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
    
    {/* Lightbox for photos */}
    {lightboxImage && (
      <div 
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={() => setLightboxImage(null)}
      >
        <button 
          onClick={() => setLightboxImage(null)}
          className="absolute top-6 right-6 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        <img 
          src={lightboxImage}
          alt="Arturo Coello"
          className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
};

// ================== FOOTER ==================
const Footer = ({ clubInfo }) => {
  return (
    <footer className="bg-slate-900 text-white py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            {/* Logo above text - saves vertical space */}
            <div className="bg-white rounded-xl p-3 inline-block mb-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_dontello-redesign/artifacts/au8d5gr7_don%20tello%20club%20de%20golf.jpg" 
                alt="Don Tello Club de Golf"
                className="h-14 w-auto"
              />
            </div>
            <h4 className="font-bold text-lg mb-2">Don Tello Pádel & Tenis</h4>
            <p className="text-slate-400 text-sm">
              Tu club de pádel y tenis en Mérida. Instalaciones de primer nivel, 
              clases para todos los niveles y el mejor ambiente deportivo.
            </p>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">Enlaces</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#campamentos" className="hover:text-white transition-colors">Campamentos</a></li>
              <li><a href="#instalaciones" className="hover:text-white transition-colors">Instalaciones</a></li>
              <li><a href="#servicios" className="hover:text-white transition-colors">Servicios</a></li>
              <li><a href="#tienda" className="hover:text-white transition-colors">Tienda</a></li>
              <li><a href="#tarifas" className="hover:text-white transition-colors">Tarifas</a></li>
              <li><a href="#contacto" className="hover:text-white transition-colors">Contacto</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">Contacto</h4>
            <ul className="space-y-2 text-slate-400">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a href={`tel:${clubInfo.phone}`} className="hover:text-white transition-colors">
                  {clubInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href={`mailto:${clubInfo.email}`} className="hover:text-white transition-colors">
                  {clubInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1" />
                <span>{clubInfo.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>L-D: 8:00h - 24:00h</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-8 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Don Tello Pádel y Tenis. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-3">
            {/* Redes sociales footer */}
            <a
              href="https://www.instagram.com/padeldontello/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-500 text-white hover:scale-110 transition-transform"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.facebook.com/alvaro.tarifaortiz"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 transition-transform"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <span className="w-px h-6 bg-slate-700 mx-2"></span>
            <a 
              href="https://www.fexpadel.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-white transition-colors text-sm"
            >
              FEX Pádel
            </a>
            <a 
              href="https://www.padelfederacion.es/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-white transition-colors text-sm"
            >
              Fed. Española Pádel
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ================== CART SIDEBAR + CHECKOUT ==================
const CartSidebar = ({ isOpen, setIsOpen, cart, removeFromCart, updateQuantity, setCart, preferredDelivery }) => {
  const [step, setStep] = useState("cart"); // "cart" | "form"
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    nif: "",
    playtomic_username: "",
    delivery_method: preferredDelivery || "pickup",
    address: "",
    city: "",
    postal_code: "",
    notes: "",
  });

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const hasBono = cart.some((item) => item.category === "bonos");
  const onlyBonos = cart.length > 0 && cart.every((item) => item.category === "bonos");
  const shippingFee = (form.delivery_method === "shipping" && !onlyBonos) ? 7.0 : 0.0;
  const total = subtotal + shippingFee;
  const base = total > 0 ? total / 1.21 : 0;
  const iva = total - base;

  useEffect(() => {
    if (!isOpen) {
      setStep("cart");
      setErrorMsg("");
      setSubmitting(false);
    }
    // Sync delivery method when cart opens (e.g., user just clicked "Comprar con envío")
    if (isOpen && preferredDelivery && preferredDelivery !== form.delivery_method) {
      setForm((f) => ({ ...f, delivery_method: preferredDelivery }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preferredDelivery]);

  if (!isOpen) return null;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const canSubmit = () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return false;
    if (hasBono && !form.playtomic_username.trim()) return false;
    if (!onlyBonos && form.delivery_method === "shipping") {
      if (!form.address.trim() || !form.city.trim() || !form.postal_code.trim()) return false;
    }
    return true;
  };

  const handleCheckout = async () => {
    setErrorMsg("");
    if (!canSubmit()) {
      setErrorMsg("Completa todos los campos obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      const items = cart.map((it) => ({
        product_id: it.id,
        variant: it.selectedVariant ? it.selectedVariant.name : null,
        options: it.selectedOptions && Object.keys(it.selectedOptions).length > 0 ? it.selectedOptions : null,
        quantity: it.quantity,
        name: "",
        unit_price: 0,
        line_total: 0,
      }));
      const effectiveDelivery = onlyBonos ? "pickup" : form.delivery_method;
      const payload = {
        items,
        customer: { ...form, delivery_method: effectiveDelivery },
        origin_url: window.location.origin,
      };
      const res = await axios.post(`${API}/checkout/session`, payload);
      if (res.data?.url) {
        // Guardamos snapshot del carrito para la página success
        localStorage.setItem("lastOrderCart", JSON.stringify(cart));
        localStorage.setItem("lastOrderCustomer", JSON.stringify(form));
        window.location.href = res.data.url;
      } else {
        setErrorMsg("No se pudo iniciar el pago. Intenta de nuevo.");
        setSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.detail || "Error al iniciar el pago. Intenta de nuevo.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-sidebar">
      <div className="absolute inset-0 bg-black/50" onClick={() => setIsOpen(false)}></div>
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-bold text-slate-900">
            {step === "cart" ? "Carrito" : "Datos de entrega"}
          </h3>
          <button
            onClick={() => setIsOpen(false)}
            data-testid="close-cart-btn"
            className="p-2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === "cart" && (
            <>
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Tu carrito está vacío</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div
                      key={item.lineId}
                      data-testid={`cart-item-${item.lineId}`}
                      className="flex gap-4 p-4 bg-slate-50 rounded-xl"
                    >
                      <img src={resolveAssetUrl(item.image)} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 text-sm">{item.name}</h4>
                        {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {Object.entries(item.selectedOptions).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                          </p>
                        )}
                        <p className="text-green-600 font-bold mt-1">{item.price}€</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateQuantity(item.lineId, item.quantity - 1)}
                            className="w-8 h-8 rounded-full bg-white border flex items-center justify-center text-slate-600 hover:bg-slate-100"
                          >-</button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.lineId, item.quantity + 1)}
                            className="w-8 h-8 rounded-full bg-white border flex items-center justify-center text-slate-600 hover:bg-slate-100"
                          >+</button>
                          <button
                            onClick={() => removeFromCart(item.lineId)}
                            className="ml-auto text-red-500 text-sm hover:underline"
                          >Eliminar</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {step === "form" && (
            <div className="space-y-4" data-testid="checkout-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nombre completo *</label>
                <input type="text" name="name" value={form.name} onChange={handleChange} data-testid="checkout-name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Email *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} data-testid="checkout-email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Teléfono *</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} data-testid="checkout-phone" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">NIF / CIF (opcional, para factura)</label>
                <input type="text" name="nif" value={form.nif} onChange={handleChange} data-testid="checkout-nif" placeholder="Ej: 12345678A" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
              </div>

              {hasBono && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="playtomic-section">
                  <label className="block text-sm font-bold text-amber-900 mb-1">Usuario de Playtomic *</label>
                  <p className="text-xs text-amber-800 mb-2">Indica el email o nombre de usuario de Playtomic donde quieres que te abonemos el saldo.</p>
                  <input type="text" name="playtomic_username" value={form.playtomic_username} onChange={handleChange} data-testid="checkout-playtomic" placeholder="tu@email.com o @usuario" className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-600" />
                </div>
              )}

              {!onlyBonos && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Entrega</label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className={`border-2 rounded-xl p-3 text-center cursor-pointer transition-all ${form.delivery_method === "pickup" ? "border-green-600 bg-green-50" : "border-slate-200"}`}>
                      <input type="radio" name="delivery_method" value="pickup" checked={form.delivery_method === "pickup"} onChange={handleChange} className="hidden" data-testid="delivery-pickup" />
                      <div className="font-bold text-sm text-slate-800">Recoger en el club</div>
                      <div className="text-xs text-green-600 font-semibold mt-1">Gratis</div>
                    </label>
                    <label className={`border-2 rounded-xl p-3 text-center cursor-pointer transition-all ${form.delivery_method === "shipping" ? "border-green-600 bg-green-50" : "border-slate-200"}`}>
                      <input type="radio" name="delivery_method" value="shipping" checked={form.delivery_method === "shipping"} onChange={handleChange} className="hidden" data-testid="delivery-shipping" />
                      <div className="font-bold text-sm text-slate-800">Envío a domicilio</div>
                      <div className="text-xs text-slate-600 mt-1">+7€</div>
                    </label>
                  </div>
                </div>
              )}

              {!onlyBonos && form.delivery_method === "shipping" && (
                <div className="space-y-3 p-3 bg-slate-50 rounded-xl">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Dirección *</label>
                    <input type="text" name="address" value={form.address} onChange={handleChange} data-testid="checkout-address" placeholder="Calle, número, piso" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Código postal *</label>
                      <input type="text" name="postal_code" value={form.postal_code} onChange={handleChange} data-testid="checkout-postal" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Ciudad *</label>
                      <input type="text" name="city" value={form.city} onChange={handleChange} data-testid="checkout-city" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Notas (opcional)</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} rows="2" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
              </div>

              {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm" data-testid="checkout-error">{errorMsg}</div>
              )}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="p-6 border-t space-y-3">
            {step === "form" && (
              <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{subtotal.toFixed(2)}€</span></div>
                <div className="flex justify-between text-slate-600"><span>Envío</span><span>{shippingFee.toFixed(2)}€</span></div>
                <div className="flex justify-between text-xs text-slate-500 pt-1 border-t border-slate-200"><span>Base (sin IVA)</span><span>{base.toFixed(2)}€</span></div>
                <div className="flex justify-between text-xs text-slate-500"><span>IVA (21%)</span><span>{iva.toFixed(2)}€</span></div>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-slate-600 font-semibold">{step === "form" ? "Total" : "Total"}</span>
              <span className="text-2xl font-black text-slate-900" data-testid="cart-total">{total.toFixed(2)}€</span>
            </div>
            {step === "cart" ? (
              <button
                onClick={() => setStep("form")}
                data-testid="checkout-btn"
                className="w-full bg-green-600 text-white py-4 rounded-full font-semibold hover:bg-green-700 transition-colors"
              >
                Continuar al pago
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setStep("cart")}
                  disabled={submitting}
                  className="flex-1 bg-slate-100 text-slate-700 py-3 rounded-full font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50"
                >Atrás</button>
                <button
                  onClick={handleCheckout}
                  disabled={submitting}
                  data-testid="confirm-checkout-btn"
                  className="flex-[2] bg-green-600 text-white py-3 rounded-full font-bold hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {submitting ? "Procesando..." : `Pagar ${total.toFixed(2)}€`}
                </button>
              </div>
            )}
            <p className="text-center text-slate-400 text-xs">Pago seguro con Stripe · Tarjeta, Apple Pay, Google Pay</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ================== CHECKOUT RESULT PAGES ==================
const CheckoutResultPage = ({ mode }) => {
  const [status, setStatus] = useState(mode === "cancel" ? "cancelled" : "polling");
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [sessionId, setSessionId] = useState(null);

  const checkStatus = async (sid, tries = 0, maxTries = 30) => {
    if (tries >= maxTries) { setStatus("timeout"); return; }
    try {
      const res = await axios.get(`${API}/checkout/status/${sid}`);
      const d = res.data;
      setDetails(d);
      setAttempts(tries + 1);
      if (d.payment_status === "paid") {
        setStatus("paid");
        localStorage.removeItem("lastOrderCart");
        localStorage.removeItem("lastOrderCustomer");
        return;
      }
      if (d.status === "expired") { setStatus("expired"); return; }
      setTimeout(() => checkStatus(sid, tries + 1, maxTries), 2000);
    } catch (e) {
      setTimeout(() => checkStatus(sid, tries + 1, maxTries), 2000);
    }
  };

  useEffect(() => {
    if (mode !== "success") return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    setSessionId(sid);
    checkStatus(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const goHome = () => { window.location.href = "/"; };

  const retryCheck = () => {
    if (!sessionId) return;
    setStatus("polling");
    setAttempts(0);
    checkStatus(sessionId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center p-4" data-testid="checkout-result-page">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl p-8 text-center">
        {mode === "success" && status === "polling" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-green-200 border-t-green-600 animate-spin"></div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Confirmando tu pago...</h2>
            <p className="text-slate-600">Un momento, por favor.</p>
            {attempts > 5 && (
              <p className="text-xs text-slate-400 mt-3">Comprobando... ({attempts}/30)</p>
            )}
          </>
        )}
        {mode === "success" && status === "paid" && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">¡Pago completado!</h2>
            <p className="text-slate-600 mb-4">Gracias por tu compra. Te hemos enviado un email con los detalles del pedido.</p>
            {details && (
              <div className="bg-slate-50 rounded-xl p-4 text-sm text-left space-y-1 mb-6" data-testid="success-details">
                <div className="flex justify-between"><span className="text-slate-600">Importe</span><span className="font-bold">{details.amount_total?.toFixed(2)}€</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Estado</span><span className="font-bold text-green-600">Pagado</span></div>
              </div>
            )}
            <button onClick={goHome} data-testid="back-home-btn" className="bg-green-600 text-white px-8 py-3 rounded-full font-bold hover:bg-green-700">Volver a la web</button>
          </>
        )}
        {mode === "success" && (status === "timeout" || status === "expired" || status === "error") && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Pago en proceso</h2>
            <p className="text-slate-600 mb-4">Estamos comprobando tu pago. Si tarda mucho, revisa tu email o contacta con nosotros.</p>
            {sessionId && (
              <p className="text-xs text-slate-400 mb-4 font-mono break-all">ID: {sessionId}</p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button onClick={retryCheck} data-testid="retry-check-btn" className="bg-yellow-500 text-white px-6 py-3 rounded-full font-bold hover:bg-yellow-600">
                Comprobar de nuevo
              </button>
              <button onClick={goHome} className="bg-green-600 text-white px-6 py-3 rounded-full font-bold hover:bg-green-700">Volver a la web</button>
            </div>
          </>
        )}
        {mode === "cancel" && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <X className="w-10 h-10 text-slate-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Pago cancelado</h2>
            <p className="text-slate-600 mb-6">Has cancelado el pago. Tu carrito sigue disponible cuando quieras completarlo.</p>
            <button onClick={goHome} data-testid="back-home-cancel-btn" className="bg-green-600 text-white px-8 py-3 rounded-full font-bold hover:bg-green-700">Volver a la web</button>
          </>
        )}
      </div>
    </div>
  );
};

// ================== WHATSAPP BUTTON ==================
const WhatsAppButton = ({ phone }) => {
  const whatsappUrl = `https://wa.me/34${phone.replace(/\s/g, "")}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-fab"
      className="fixed bottom-6 right-6 z-50 whatsapp-btn p-4 rounded-full text-white"
      aria-label="Contactar por WhatsApp"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
  );
};

// ================== MAIN APP ==================
function App() {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [clubInfo, setClubInfo] = useState({});
  const [promoBanner, setPromoBanner] = useState(null);
  const [siteContent, setSiteContent] = useState({ campamentos_enabled: true, pricing_pdf_url: null });
  const [campContent, setCampContent] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [preferredDelivery, setPreferredDelivery] = useState("pickup");
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, servicesRes, pricingRes, facilitiesRes, clubInfoRes, bannerRes, siteContentRes, campRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/services`),
          axios.get(`${API}/pricing`),
          axios.get(`${API}/facilities`),
          axios.get(`${API}/club-info`),
          axios.get(`${API}/promo-banner`),
          axios.get(`${API}/site-content`),
          axios.get(`${API}/camp-content`),
        ]);
        setProducts(productsRes.data);
        setServices(servicesRes.data);
        setPricing(pricingRes.data);
        setFacilities(facilitiesRes.data);
        setClubInfo(clubInfoRes.data);
        setPromoBanner(bannerRes.data);
        setSiteContent(siteContentRes.data);
        setCampContent(campRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Intersection Observer for active section
  useEffect(() => {
    const sections = ["campamentos", "instalaciones", "servicios", "tienda", "tarifas", "contacto"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const addToCart = (product, deliveryMethod) => {
    if (deliveryMethod) setPreferredDelivery(deliveryMethod);
    const variantKey = product.selectedVariant ? `-v:${product.selectedVariant.name}` : "";
    const optionsKey = product.selectedOptions && Object.keys(product.selectedOptions).length > 0
      ? "-o:" + Object.entries(product.selectedOptions).map(([k, v]) => `${k}=${v}`).join("|")
      : "";
    const lineId = `${product.id}${variantKey}${optionsKey}`;
    setCart((prev) => {
      const existing = prev.find((item) => item.lineId === lineId);
      if (existing) {
        return prev.map((item) =>
          item.lineId === lineId ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, lineId, quantity: 1 }];
    });
    if (deliveryMethod) setShowCart(true);
  };

  const removeFromCart = (lineId) => {
    setCart((prev) => prev.filter((item) => item.lineId !== lineId));
  };

  const updateQuantity = (lineId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(lineId);
      return;
    }
    setCart((prev) =>
      prev.map((item) => (item.lineId === lineId ? { ...item, quantity } : item))
    );
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Simple routing for checkout result pages
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  if (pathname.startsWith("/admin")) {
    return <AdminApp />;
  }
  if (pathname.startsWith("/checkout/success")) {
    return <CheckoutResultPage mode="success" />;
  }
  if (pathname.startsWith("/checkout/cancel")) {
    return <CheckoutResultPage mode="cancel" />;
  }

  return (
    <div className="App" data-testid="app-root">
      <Header 
        activeSection={activeSection} 
        setShowCart={setShowCart} 
        cartCount={cartCount} 
      />
      <Hero onBuyBono={() => {
        const bono = products.find((p) => p.id === "bono-playtomic-20");
        if (bono) {
          addToCart(bono);
          setShowCart(true);
        } else {
          document.getElementById("tienda")?.scrollIntoView({ behavior: "smooth" });
        }
      }} />
      <PromoBanner banner={promoBanner} />
      {siteContent.campamentos_enabled !== false && campContent && <SummerCamp content={campContent} />}
      <Facilities facilities={facilities} />
      <Services services={services} />
      <Shop products={products} addToCart={addToCart} />
      <Pricing pricing={pricing} pricingPdfUrl={siteContent.pricing_pdf_url} />
      <Contact clubInfo={clubInfo} />
      <AboutUs />
      <Footer clubInfo={clubInfo} />
      <CartSidebar
        isOpen={showCart}
        setIsOpen={setShowCart}
        cart={cart}
        setCart={setCart}
        removeFromCart={removeFromCart}
        updateQuantity={updateQuantity}
        preferredDelivery={preferredDelivery}
      />
      <WhatsAppButton phone={clubInfo.phone || "639713537"} />
    </div>
  );
}

export default App;
